import { css } from "styled-components";

import {
    colorCSS,
    colorContrastCSS,
    headingColorContrastCSS,
    borderColorCSS,
    colorExists,
    colorIsDark
} from "./colors";
import {
    gradientCSS,
    gradientContrastCSS,
    gradientExists,
    gradientIsDark
} from "./gradients";
import { redundantCssFromKebab } from "./redundant-css";

export type Colors = {
    color?: string;
    background?: string;
    medium?: string;
    headingColor?: string;
    borderColor?: string;
    linkColor?: string;
    linkHoverColor?: string;
};

export type BackgroundFieldValue = {
    readonly color?: string;
    readonly gradient?: string;
    readonly angle?: number;
};

export function isBackgroundFieldValue(
    value: unknown
): value is BackgroundFieldValue {
    return !!value;
}

//background json could be { gradient: 'GR234' }
export function getColorsFromBackgroundField(background: unknown): Colors {
    if (isBackgroundFieldValue(background)) {
        const { gradient, angle, color } = background;
        if (gradient && gradientExists(gradient)) {
            return {
                background: gradientCSS(gradient, angle),
                color: gradientContrastCSS(gradient),
                medium: gradientContrastCSS(gradient, 0.2),
                linkColor: gradientIsDark(gradient)
                    ? colorCSS("N0", 0.8)
                    : colorCSS("B400"),
                linkHoverColor: gradientIsDark(gradient)
                    ? colorCSS("N0")
                    : colorCSS("B500")
            };
        } else if (color && colorExists(color)) {
            return {
                background: colorCSS(color),
                color: colorContrastCSS(color),
                medium: colorContrastCSS(color, 0.2),
                headingColor: headingColorContrastCSS(color),
                borderColor: borderColorCSS(color),
                linkColor: colorIsDark(color)
                    ? colorCSS("N0", 0.8)
                    : colorCSS("B400"),
                linkHoverColor: colorIsDark(color)
                    ? colorCSS("N0")
                    : colorCSS("B500")
            };
        }
    }
    return {};
}

export function cssColorsFromBackgroundField(
    backgroundField: unknown
): ReturnType<typeof css> | null {
    if (!isBackgroundFieldValue(backgroundField)) {
        return null;
    }
    const { background, color, linkColor, linkHoverColor } =
        getColorsFromBackgroundField(backgroundField);

    if (backgroundField.gradient) {
        return css`
            ${redundantCssFromKebab("background", `${background}`)}
            ${redundantCssFromKebab("color", `${color}`)}

            a {
                ${redundantCssFromKebab("color", `${linkColor}`)}
                &:hover {
                    ${redundantCssFromKebab("color", `${linkHoverColor}`)}
                }
            }

            *:focus {
                ${redundantCssFromKebab("outline", `2px solid ${color}`)}
                &:not(:focus-visible) {
                    ${redundantCssFromKebab("outline", "0 !important")}
                }
            }
        `;
    } else {
        return css`
            ${redundantCssFromKebab("background-color", `${background}`)}
            ${redundantCssFromKebab("color", `${color}`)}

            a {
                ${redundantCssFromKebab("color", `${linkColor}`)}
                &:hover {
                    ${redundantCssFromKebab("color", `${linkHoverColor}`)}
                }
            }

            *:focus {
                ${redundantCssFromKebab("outline", `2px solid ${color}`)}
                &:not(:focus-visible) {
                    ${redundantCssFromKebab("outline", "0 !important")}
                }
            }
        `;
    }
}
